import {
  ISimpleSoundPlayer,
  ISimpleSoundPlayerType,
  SimpleSoundPlayer,
} from 'app/components/rtc/SimpleSoundPlayer';
import TaskQueue from 'app/utils/queues/TaskQueue';
import React, { Component, ReactNode, RefObject, useContext } from 'react';
import { assetBaseURL } from 'app/config/client';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { BookingTypeSettings } from 'app/models/rtc/booking/constants';

export interface INotifier<T> {
  notify(name: T): void;
}

type SoundValues<T extends string> = {
  [k in T]: string;
};

type SoundPlayerRefs<T extends string> = {
  [k in T]: RefObject<ISimpleSoundPlayer>;
};

interface IProps<T extends string> {
  children: (manager: INotifier<T>) => ReactNode;
  sounds: SoundValues<T>;
  soundPlayerType: ISimpleSoundPlayerType;
  isActive: boolean;
}

interface IState<T extends string> {
  sounds?: SoundValues<T>;
  soundPlayerRefs?: SoundPlayerRefs<T>;
}

export class Notifier<T extends string>
  extends Component<IProps<T>, IState<T>>
  implements INotifier<T> {
  public static getDerivedStateFromProps<NextProps extends string>(
    props: IProps<NextProps>, state: IState<NextProps>,
  ): Partial<IState<NextProps>> | null {
    if (state.sounds !== props.sounds) {
      const newSoundPlayers: SoundPlayerRefs<NextProps> = {} as SoundPlayerRefs<NextProps>;

      (Object.keys(props.sounds) as NextProps[]).forEach((name: NextProps) => {
        newSoundPlayers[name] = React.createRef<ISimpleSoundPlayer>();
      });

      return {
        sounds:          props.sounds,
        soundPlayerRefs: newSoundPlayers,
      };
    }

    return null;
  }

  private soundQueue: T[] = [];
  private soundQueueRunner = new TaskQueue();

  private proxy: INotifier<T> = {
    notify: (name: T): Promise<void> => this.notify(name),
  };

  public constructor(initialProps: IProps<T>) {
    super(initialProps);
    this.state = {};
  }

  public render() {
    const {
      props: { sounds, isActive, soundPlayerType: SoundPlayerType },
      state: { soundPlayerRefs },
    } = this;

    return (
      <React.Fragment>
        {isActive && (Object.keys(sounds) as T[]).map((name: T) => {
          const src = sounds[name];
          const ref = soundPlayerRefs![name];
          return (
            <SoundPlayerType
              key={name}
              src={src}
              ref={ref}
            />
          );
        })}

        {this.props.children(this.proxy)}
      </React.Fragment>
    );
  }

  public async notify(name: T): Promise<void> {
    if (
      this.soundQueue.length !== 0 &&
      this.soundQueue[this.soundQueue.length - 1] === name
    ) {
      return;
    }

    this.soundQueue.push(name);

    await this.soundQueueRunner.push(async () => {
      await this.playSoundFromQueue();
    });
  }

  private async playSoundFromQueue(): Promise<void> {
    const name   = this.soundQueue.shift()!;
    const player = this.state.soundPlayerRefs![name];
    if (player && player.current) {
      await player.current.play();
    }
  }
}

export enum RtcNotifications {
  Join =        'join',
  Part =        'part',
}

const RTC_NOTIFICATION_SOUNDS = {
  [RtcNotifications.Join]: `${assetBaseURL}/sounds/rtc/peer-joined.mp3`,
  [RtcNotifications.Part]: `${assetBaseURL}/sounds/rtc/peer-left.mp3`,
};

const rtcNotifierContext = React.createContext<INotifier<RtcNotifications> | undefined>(undefined);
const {
  Provider,
  Consumer,
} = rtcNotifierContext;

export const useRtcNotifier = () => useContext(rtcNotifierContext);

export const RtcNotifierProvider = ({ children }: { children: ReactNode }) => {
  const bookingState = useBookingState();
  const { rtcRoom } = bookingState || { rtcRoom: null };
  const enableSounds =
    Boolean(bookingState && BookingTypeSettings[bookingState.bookingType].enableSoundOnPresenceChange);

  return <Notifier<RtcNotifications>
    isActive={Boolean(rtcRoom) && enableSounds}
    sounds={RTC_NOTIFICATION_SOUNDS}
    soundPlayerType={SimpleSoundPlayer}
  >{(manager) => (
    <Provider value={manager}>
      {children}
    </Provider>
  )}
  </Notifier>;
};

export const GetRtcNotifier = Consumer;
